import React, { useReducer } from "react";

import clsx from "clsx";

import type * as UDM from "@busbud/int-schemas/udm";

import { Bus } from "@app/components/bus/bus";
import { Drawer } from "@app/components/bus/drawer";
import FloorSelector from "@app/components/bus/floor-selector/floor-selector";
import { SeatLegend } from "@app/components/bus/seat-legend";
import { SeatSelectionDetailsMode } from "@app/constants/seat-selection-details-modes";
import { useAppContext, useAppDispatch } from "@app/helpers/hooks";
import { getActiveFloorIndex } from "@app/helpers/udm/vehicle";
import { updateCartTripSeatsSelected } from "@app/modules/checkout/store/v2/thunks/cart";
import { addToCart } from "@app/modules/results/actions";
import { pickedGenderBusDetails } from "@app/tracking/results-tracking";
import type { CurrencyCode } from "@app/types/currency";
import type { SearchInfo } from "@app/types/search-types";
import type {
  GetPassengerSeat,
  OnPassengerSelected,
  OnSeatSelected,
  PassengerSeatSelectionInstance,
  SeatInfo,
  UpdatePassenger
} from "@app/types/seat-selection-types";

import type { SeatPrices } from "../seat-price";
import type { AnyAction } from "@reduxjs/toolkit";

import { useStyles } from "./seat-selector.styles";
import { createPassenger, setPassengerSeat } from "./seat-selector.utils";
import { SummaryInline } from "../summary-inline";

export type SelectedSeatsInfo = { [id: string]: SeatInfo };
export type Direction = "outbound" | "return";

interface Props {
  direction: Direction;
  mode: SeatSelectionDetailsMode;
  renderLegend?:
    | ((
        bus: UDM.Bus,
        passengers: PassengerSeatSelectionInstance[],
        getPassengerSeat: GetPassengerSeat
      ) => React.ReactNode)
    | React.ReactNode;
  renderSummary?: (
    segment_id: string,
    seats_info: SelectedSeatsInfo,
    passengers: PassengerSeatSelectionInstance[],
    getPassengerSeat: GetPassengerSeat,
    onConfirm: () => void
  ) => React.ReactNode;
  renderBus?: (
    bus: UDM.Bus,
    other_assigned_seats_ids: (string | null)[],
    active_seat_id: string | null,
    passengers: PassengerSeatSelectionInstance[],
    passenger_index: number,
    onPassengerSelected: OnPassengerSelected,
    onSeatSelected: OnSeatSelected,
    active_floor: number,
    is_checkout?: boolean
  ) => React.ReactNode;
  search: SearchInfo;
  currency: CurrencyCode;
  is_external: boolean;
  trip_id: string;
  segment_id: string;
  vehicle: UDM.Bus | null;
  prices: SeatPrices;
  departure_time?: string;
  initial_passengers?: PassengerSeatSelectionInstance[];
  initial_selected_seats?: SelectedSeatsInfo;
  is_checkout?: boolean;
  passenger_index?: number;
  onConfirm?: (
    passengers: PassengerSeatSelectionInstance[],
    trip_id: string
  ) => void;
}

const initPassengers = (search: SearchInfo) => {
  const passengers: PassengerSeatSelectionInstance[] = [];
  for (let i = 0; i < search.adult; i++) {
    const passenger = createPassenger();
    passengers.push(passenger);
  }
  for (let i = 0; i < search.child; i++) {
    const passenger = createPassenger({
      passenger_category: "youth"
    });
    passengers.push(passenger);
  }
  for (let i = 0; i < search.senior; i++) {
    const passenger = createPassenger({
      passenger_category: "senior"
    });
    passengers.push(passenger);
  }

  return passengers;
};

function reducer(state: State, action: AnyAction) {
  switch (action.type) {
    case "seat_selected":
      return { ...state, ...action.data };
    case "passenger_selected":
      return {
        ...state,
        active_seat_id: null,
        ...action.data
      };
    case "active_floor_changed": {
      return {
        ...state,
        active_floor: action.data
      };
    }
  }
}

export interface State {
  active_seat_id: string | null;
  active_floor: number;
  passengers: PassengerSeatSelectionInstance[];
  passenger_index: number;
  seats_info: SelectedSeatsInfo;
}

export const SeatSelector: React.FC<Props> = props => {
  const {
    vehicle,
    trip_id,
    prices,
    direction,
    search,
    mode,
    segment_id,
    initial_passengers,
    initial_selected_seats,
    is_checkout
  } = props;
  const { tracker, api, translator } = useAppContext();

  const getPassengerSeat = (
    passenger: PassengerSeatSelectionInstance
  ): string | null =>
    direction === "outbound"
      ? passenger.outbound_selected_seat
      : passenger.return_selected_seat;

  const initial_passengers_state = initial_passengers || initPassengers(search);
  const already_assigned_seats_ids: (string | null)[] =
    initial_passengers_state.map((passenger: PassengerSeatSelectionInstance) =>
      getPassengerSeat(passenger)
    );

  const initial_state: State = {
    active_seat_id: null,
    passengers: initial_passengers_state,
    passenger_index: props.passenger_index || 0,
    active_floor: getActiveFloorIndex(
      already_assigned_seats_ids,
      props.vehicle?.layout
    ),
    seats_info: initial_selected_seats || {}
  };

  const [state, dispatch] = useReducer(reducer, initial_state);
  const reduxStateDispatch = useAppDispatch();

  const resetPassenger = (
    passenger: PassengerSeatSelectionInstance,
    segment_id?: string
  ): PassengerSeatSelectionInstance => {
    const { passenger_category, selected_seats } = passenger;

    return createPassenger({
      passenger_category,
      selected_seats: selected_seats.filter(
        seat => seat.segment_id !== segment_id
      )
    });
  };

  const getUnassignedPassengerIndex = (
    passengers: PassengerSeatSelectionInstance[]
  ): number =>
    passengers.findIndex(passenger => getPassengerSeat(passenger) === null);

  const getPassengerIndexBySeatId = (id: string): number =>
    state.passengers.findIndex(
      (passenger: PassengerSeatSelectionInstance) =>
        (direction === "outbound" && passenger.outbound_selected_seat === id) ||
        (direction === "return" && passenger.return_selected_seat === id)
    );

  const onSeatSelected = (
    seat: UDM.SeatCell | null,
    { has_popover }: { has_popover: boolean }
  ) => {
    if (!seat) {
      return;
    }

    const passengers: PassengerSeatSelectionInstance[] = [...state.passengers];
    const index = getPassengerIndexBySeatId(seat.id);
    const has_selected_seat = index < 0;

    if (!has_selected_seat) {
      // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      passengers[index] = resetPassenger(passengers[index], segment_id);
    } else {
      passengers[state.passenger_index] = setPassengerSeat(
        // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        passengers[state.passenger_index],
        seat.id,
        segment_id,
        direction
      );
    }

    const is_popup_open = has_popover && has_selected_seat;
    const active_seat_id = is_popup_open ? seat.id : null;

    const unassigned_passenger_index = getUnassignedPassengerIndex(passengers);
    const has_passenger_to_assign = unassigned_passenger_index > -1;
    const passenger_index =
      typeof props.passenger_index === "number"
        ? props.passenger_index
        : has_passenger_to_assign && !is_popup_open
          ? unassigned_passenger_index
          : state.passenger_index;

    const seats_info = { ...state.seats_info };
    seats_info[seat.id] = {
      prices: seat.prices,
      gender: seat.gender,
      display_name: seat.display_name
    };

    dispatch({
      type: "seat_selected",
      data: {
        active_seat_id,
        passengers,
        passenger_index,
        seats_info
      }
    });
  };

  const onPassengerSelected = (updatePassenger: UpdatePassenger) => {
    const index = state.passenger_index;
    const passengers = [...state.passengers];
    // @ts-expect-error TS2345 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    passengers[index] = updatePassenger(passengers[index], createPassenger);

    const {
      outbound_selected_seat,
      questions: { gender }
    } = passengers[index];

    if (outbound_selected_seat && gender) {
      tracker.track(
        pickedGenderBusDetails(
          tracker.analytics,
          outbound_selected_seat,
          gender
        )
      );
    }

    const passenger_index =
      typeof props.passenger_index === "number"
        ? props.passenger_index
        : getUnassignedPassengerIndex(state.passengers);

    dispatch({
      type: "passenger_selected",
      data: {
        passengers,
        passenger_index:
          passenger_index > -1 ? passenger_index : state.passenger_index
      }
    });
  };

  const onConfirm = () => {
    if (props.onConfirm) {
      props.onConfirm(state.passengers, trip_id);
      return;
    }

    // TODO-ASYNC-CART-HARD-ADOPT: remove it
    if (is_checkout) {
      reduxStateDispatch(
        updateCartTripSeatsSelected({
          api,
          trip_id,
          segment_id,
          seat_selector_passengers: state.passengers,
          translator
        })
      );
    } else {
      reduxStateDispatch(addToCart(trip_id, state.passengers));
    }
  };

  if (!vehicle) {
    return null;
  }

  const renderLegend = (vehicle: UDM.Bus) => {
    if (typeof props.renderLegend === "function") {
      return props.renderLegend(vehicle, state.passengers, getPassengerSeat);
    }

    return props.renderLegend || <SeatLegend spacing={2} />;
  };

  const renderBus = (bus: UDM.Bus) => {
    const other_assigned_seats_ids: (string | null)[] = state.passengers.map(
      (passenger: PassengerSeatSelectionInstance) => getPassengerSeat(passenger)
    );
    if (props.renderBus) {
      return props.renderBus(
        bus,
        other_assigned_seats_ids,
        state.active_seat_id,
        state.passengers,
        state.passenger_index,
        onPassengerSelected,
        onSeatSelected,
        state.active_floor,
        is_checkout
      );
    }

    return (
      <Bus
        orientation={mode === SeatSelectionDetailsMode.Inline ? "left" : "top"}
        key={`seat-selection-${trip_id}`}
        vehicle_layout={bus.layout}
        vehicle_driver={bus.driver}
        already_assigned_seat_ids={other_assigned_seats_ids}
        active_seat_id={state.active_seat_id}
        onSeatChange={seat => onSeatSelected(seat, { has_popover: false })}
        is_checkout={is_checkout}
        active_floor={state.active_floor}
      />
    );
  };

  const renderSummary = () => {
    if (typeof props.renderSummary === "function") {
      return props.renderSummary(
        segment_id,
        state.seats_info,
        state.passengers,
        getPassengerSeat,
        onConfirm
      );
    }

    const common_props = {
      currency: props.currency,
      onConfirm,
      selected_seats: getSelectedSeatsList(state.seats_info, state.passengers),
      passengers: state.passengers,
      segment_id,
      is_external: props.is_external,
      prices,
      passenger_index: props.passenger_index
    };

    // // Only NN is displaying Seat Selection on Result's pages
    // // if needed, we will have to redesign for BB when needed as per below
    return mode === SeatSelectionDetailsMode.Inline ? (
      <SummaryInline {...common_props} />
    ) : (
      <Drawer {...common_props} disabled={false} />
    );
  };

  const getSelectedSeatsList = (
    seats_info: SelectedSeatsInfo,
    passengers: PassengerSeatSelectionInstance[]
  ): UDM.SeatCell[] =>
    passengers.reduce<UDM.SeatCell[]>((seats, passenger) => {
      const seat_id = getPassengerSeat(passenger);

      if (seat_id) {
        seats.push({
          ...seats_info[seat_id],
          type: "seat",
          id: seat_id,
          // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
          display_name: seats_info[seat_id].display_name || seat_id,
          availability: true,
          amenities: []
        });
      }

      return seats;
    }, []);

  const renderFloorSelector = () => {
    const show_floor_selector =
      props.is_checkout && vehicle.layout && vehicle.layout.length > 1;

    if (!show_floor_selector) {
      return null;
    }

    return (
      <FloorSelector
        active_floor={state.active_floor}
        handleChange={index =>
          dispatch({ type: "active_floor_changed", data: index })
        }
      />
    );
  };

  const Component =
    mode === SeatSelectionDetailsMode.Inline
      ? SeatSelectorInline
      : SeatSelectorModal;

  return (
    <Component
      renderLegend={() => renderLegend(vehicle)}
      renderBus={() => renderBus(vehicle)}
      renderSummary={renderSummary}
      renderFloorSelector={renderFloorSelector}
      isCheckout={is_checkout}
    />
  );
};

interface SeatSelectorComponentProps {
  className?: string;
  renderLegend: () => React.ReactNode;
  renderBus: () => React.ReactNode;
  renderSummary: () => React.ReactNode;
  renderFloorSelector: () => React.ReactNode;
  isCheckout?: boolean;
}

const SeatSelectorModal: React.FC<SeatSelectorComponentProps> = ({
  className,
  renderLegend,
  renderBus,
  renderSummary,
  renderFloorSelector,
  isCheckout
}) => {
  const { cx, classes } = useStyles();

  return (
    <div
      className={cx(classes.drawerContainer, className, {
        [classes.checkoutDrawerContainer]: isCheckout
      })}
    >
      <div>{renderFloorSelector()}</div>
      <div
        className={cx(classes.drawerlegend, classes.legendContainer, {
          [classes.checkoutDrawerlegend]: isCheckout
        })}
      >
        {renderLegend()}
      </div>
      <div className={classes.busContainer}>{renderBus()}</div>
      <div className={classes.summaryContainer}>{renderSummary()}</div>
    </div>
  );
};

const SeatSelectorInline: React.FC<SeatSelectorComponentProps> = ({
  className,
  renderLegend,
  renderBus,
  renderSummary
}) => {
  const { cx, classes } = useStyles();

  return (
    <div
      data-borderred="true"
      className={clsx("flex gap-300 xl:gap-500", className, classes.container)}
    >
      <div
        data-borderred="true"
        className={clsx("flex flex-col gap-200", classes.busContainerAndLegend)}
      >
        {renderBus()}
        <div className={classes.legendContainer}>{renderLegend()}</div>
      </div>
      <div className={cx(classes.summaryContainer, classes.summaryInline)}>
        {renderSummary()}
      </div>
    </div>
  );
};
